import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Trash2, Mail, Calendar } from 'lucide-react';
import BenevolCheckLogo from '../components/BenevolCheckLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fonction pour calculer le statut de renouvellement
const getRenewalStatus = (renewalDate) => {
  if (!renewalDate) return { status: 'unknown', label: 'Non défini', color: 'bg-gray-100 text-gray-800' };
  
  const now = new Date();
  const renewal = new Date(renewalDate);
  const diffDays = Math.ceil((renewal - now) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) {
    return { status: 'expired', label: 'Expiré', color: 'bg-red-100 text-red-800', days: diffDays };
  } else if (diffDays <= 30) {
    return { status: 'warning', label: `${diffDays}j restants`, color: 'bg-orange-100 text-orange-800', days: diffDays };
  } else if (diffDays <= 90) {
    return { status: 'soon', label: `${diffDays}j restants`, color: 'bg-yellow-100 text-yellow-800', days: diffDays };
  } else {
    return { status: 'ok', label: `${diffDays}j restants`, color: 'bg-green-100 text-green-800', days: diffDays };
  }
};

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [associations, setAssociations] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      
      const [statsRes, assocRes, volRes, certRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/associations`, { headers }),
        axios.get(`${API}/admin/volunteers`, { headers }),
        axios.get(`${API}/admin/certificates`, { headers }),
        axios.get(`${API}/admin/pending-accounts`, { headers })
      ]);
      
      setStats(statsRes.data);
      setAssociations(assocRes.data);
      setVolunteers(volRes.data);
      setCertificates(certRes.data);
      setPendingAccounts(pendingRes.data);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveAccount = async (userId) => {
    setActionLoading(userId);
    try {
      const headers = getAuthHeader();
      await axios.post(`${API}/admin/approve-account/${userId}`, {}, { headers });
      setPendingAccounts(prev => prev.filter(acc => acc.id !== userId));
      // Rafraîchir les données
      fetchData();
    } catch (error) {
      console.error('Erreur lors de l\'approbation:', error);
      alert('Erreur lors de l\'approbation du compte');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectAccount = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir rejeter ce compte ? Cette action est irréversible.')) {
      return;
    }
    setActionLoading(userId);
    try {
      const headers = getAuthHeader();
      await axios.post(`${API}/admin/reject-account/${userId}`, {}, { headers });
      setPendingAccounts(prev => prev.filter(acc => acc.id !== userId));
    } catch (error) {
      console.error('Erreur lors du rejet:', error);
      alert('Erreur lors du rejet du compte');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteAccount = async (userId, userName, userRole = 'association_admin') => {
    const roleLabel = userRole === 'volunteer' ? 'ce bénévole' : 'ce responsable associatif';
    const extraWarning = userRole === 'volunteer' ? '\n\nToutes ses attestations seront également supprimées.' : '';
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le compte de "${userName}" (${roleLabel}) ?${extraWarning}\n\nCette action est irréversible.`)) {
      return;
    }
    setActionLoading(userId);
    try {
      const headers = getAuthHeader();
      await axios.delete(`${API}/admin/delete-account/${userId}`, { headers });
      
      if (userRole === 'volunteer') {
        setVolunteers(prev => prev.filter(v => v.id !== userId));
      } else {
        setAssociations(prev => prev.filter(assoc => assoc.id !== userId));
      }
      
      alert(`Le compte de ${userName} a été supprimé avec succès.`);
      fetchData(); // Refresh stats
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la suppression du compte');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteCertificate = async (certId, volunteerName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'attestation de "${volunteerName}" ?\n\nCette action est irréversible.`)) {
      return;
    }
    setActionLoading(certId);
    try {
      const headers = getAuthHeader();
      await axios.delete(`${API}/admin/delete-certificate/${certId}`, { headers });
      setCertificates(prev => prev.filter(c => c.id !== certId));
      alert(`L'attestation a été supprimée avec succès.`);
      fetchData(); // Refresh stats
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la suppression de l\'attestation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendRenewalReminder = async (userId, associationName, email) => {
    if (!window.confirm(`Envoyer un email de rappel de renouvellement à "${associationName}" (${email}) ?`)) {
      return;
    }
    setActionLoading(`renewal-${userId}`);
    try {
      const headers = getAuthHeader();
      await axios.post(`${API}/admin/associations/${userId}/send-renewal-reminder`, {}, { headers });
      alert(`Email de rappel envoyé avec succès à ${email}`);
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      alert(error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'email');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateRenewalDate = async (userId, currentDate) => {
    const newDate = window.prompt(
      'Entrez la nouvelle date de renouvellement (format: AAAA-MM-JJ):',
      currentDate ? new Date(currentDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    );
    
    if (!newDate) return;
    
    // Valider le format de la date
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
      alert('Format de date invalide. Utilisez le format AAAA-MM-JJ (ex: 2025-12-31)');
      return;
    }
    
    setActionLoading(`date-${userId}`);
    try {
      const headers = getAuthHeader();
      await axios.put(`${API}/admin/associations/${userId}/renewal-date`, 
        { renewal_date: new Date(newDate).toISOString() }, 
        { headers }
      );
      // Mettre à jour localement
      setAssociations(prev => prev.map(assoc => 
        assoc.id === userId ? { ...assoc, renewal_date: new Date(newDate).toISOString() } : assoc
      ));
      alert('Date de renouvellement mise à jour');
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800" data-testid="super-admin-dashboard">
      {/* Header */}
      <header className="bg-gray-800 shadow-lg border-b border-gray-700">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <BenevolCheckLogo size="md" />
              <Badge className="bg-red-600 text-white">Super Admin</Badge>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-300 text-sm">{user?.email}</span>
              <Button variant="outline" size="sm" onClick={handleLogout} className="text-gray-300 border-gray-600 hover:bg-gray-700">
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-400">{stats.total_associations}</div>
                  <div className="text-gray-400 text-sm mt-1">Associations</div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">{stats.total_volunteers}</div>
                  <div className="text-gray-400 text-sm mt-1">Bénévoles</div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400">{stats.total_certificates}</div>
                  <div className="text-gray-400 text-sm mt-1">Attestations</div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-emerald-400">{stats.validated_certificates}</div>
                  <div className="text-gray-400 text-sm mt-1">Validées</div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">{stats.pending_certificates}</div>
                  <div className="text-gray-400 text-sm mt-1">En attente</div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-gray-800 border border-gray-700 mb-6">
            <TabsTrigger value="overview" className="data-[state=active]:bg-gray-700" data-testid="tab-overview">
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-gray-700 relative" data-testid="tab-pending">
              En attente
              {pendingAccounts.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {pendingAccounts.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="associations" className="data-[state=active]:bg-gray-700" data-testid="tab-associations">
              Associations ({associations.length})
            </TabsTrigger>
            <TabsTrigger value="volunteers" className="data-[state=active]:bg-gray-700" data-testid="tab-volunteers">
              Bénévoles ({volunteers.length})
            </TabsTrigger>
            <TabsTrigger value="certificates" className="data-[state=active]:bg-gray-700" data-testid="tab-certificates">
              Attestations ({certificates.length})
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Recent Associations */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Dernières associations inscrites</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {associations.slice(0, 5).map((assoc, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-gray-700 rounded-lg">
                        <div>
                          <div className="text-white font-medium">{assoc.association_name || 'N/A'}</div>
                          <div className="text-gray-400 text-sm">{assoc.email}</div>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="text-blue-400 border-blue-400">
                            {assoc.certificates_count} attestations
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {associations.length === 0 && (
                      <p className="text-gray-400 text-center py-4">Aucune association inscrite</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Certificates */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Dernières attestations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {certificates.slice(0, 5).map((cert, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-gray-700 rounded-lg">
                        <div>
                          <div className="text-white font-medium">{cert.volunteer_name}</div>
                          <div className="text-gray-400 text-sm">{cert.association_name}</div>
                        </div>
                        <Badge className={cert.status === 'validated' ? 'bg-green-600' : 'bg-yellow-600'}>
                          {cert.status === 'validated' ? 'Validée' : 'En attente'}
                        </Badge>
                      </div>
                    ))}
                    {certificates.length === 0 && (
                      <p className="text-gray-400 text-center py-4">Aucune attestation</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pending Accounts Tab */}
          <TabsContent value="pending">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Comptes en attente de validation ({pendingAccounts.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {pendingAccounts.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">✅ Aucun compte en attente de validation</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingAccounts.map((account, idx) => (
                      <div key={idx} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 bg-gray-700 rounded-lg border border-yellow-600/30">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white font-medium">{account.name}</span>
                            <Badge className="bg-yellow-600 text-xs">En attente</Badge>
                          </div>
                          <div className="text-gray-400 text-sm">{account.email}</div>
                          {account.association_name && (
                            <div className="text-blue-400 text-sm mt-1">🏢 {account.association_name}</div>
                          )}
                          <div className="text-gray-500 text-xs mt-1">
                            Inscrit le {formatDate(account.created_at)}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApproveAccount(account.id)}
                            disabled={actionLoading === account.id}
                            data-testid={`approve-${account.id}`}
                          >
                            {actionLoading === account.id ? '...' : '✓ Approuver'}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRejectAccount(account.id)}
                            disabled={actionLoading === account.id}
                            data-testid={`reject-${account.id}`}
                          >
                            {actionLoading === account.id ? '...' : '✗ Rejeter'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Associations Tab */}
          <TabsContent value="associations">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Toutes les associations ({associations.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Association</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Responsable</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Email</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Attestations</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Inscription</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Renouvellement</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {associations.map((assoc, idx) => {
                        const renewalStatus = getRenewalStatus(assoc.renewal_date);
                        return (
                          <tr key={idx} className="border-b border-gray-700 hover:bg-gray-700/50">
                            <td className="py-3 px-4">
                              <div className="text-white font-medium">{assoc.association_name || 'N/A'}</div>
                              <div className="text-gray-500 text-xs">{assoc.association_address || '-'}</div>
                            </td>
                            <td className="py-3 px-4 text-gray-300">{assoc.name}</td>
                            <td className="py-3 px-4 text-gray-300 text-sm">{assoc.email}</td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-green-400">{assoc.validated_count}</span>
                              <span className="text-gray-500"> / </span>
                              <span className="text-blue-400">{assoc.certificates_count}</span>
                            </td>
                            <td className="py-3 px-4 text-gray-400 text-sm">{formatDate(assoc.created_at)}</td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <Badge className={`${renewalStatus.color} text-xs`}>
                                  {renewalStatus.label}
                                </Badge>
                                {assoc.renewal_date && (
                                  <span className="text-gray-500 text-xs">
                                    {formatDate(assoc.renewal_date)}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                                  onClick={() => handleUpdateRenewalDate(assoc.id, assoc.renewal_date)}
                                  disabled={actionLoading === `date-${assoc.id}`}
                                  title="Modifier la date de renouvellement"
                                >
                                  {actionLoading === `date-${assoc.id}` ? '...' : <Calendar size={16} />}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-orange-400 hover:text-orange-300 hover:bg-orange-900/30"
                                  onClick={() => handleSendRenewalReminder(assoc.id, assoc.association_name, assoc.email)}
                                  disabled={actionLoading === `renewal-${assoc.id}` || !assoc.renewal_date}
                                  title="Envoyer un rappel de renouvellement"
                                >
                                  {actionLoading === `renewal-${assoc.id}` ? '...' : <Mail size={16} />}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                                  onClick={() => handleDeleteAccount(assoc.id, assoc.name)}
                                  disabled={actionLoading === assoc.id}
                                  title="Supprimer le compte"
                                  data-testid={`delete-assoc-${assoc.id}`}
                                >
                                  {actionLoading === assoc.id ? '...' : <Trash2 size={16} />}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {associations.length === 0 && (
                    <p className="text-gray-400 text-center py-8">Aucune association inscrite</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Volunteers Tab */}
          <TabsContent value="volunteers">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Tous les bénévoles ({volunteers.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Nom</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Email</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Attestations</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Inscription</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {volunteers.map((vol, idx) => (
                        <tr key={idx} className="border-b border-gray-700 hover:bg-gray-700/50">
                          <td className="py-3 px-4 text-white font-medium">{vol.name}</td>
                          <td className="py-3 px-4 text-gray-300">{vol.email}</td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-green-400">{vol.validated_count}</span>
                            <span className="text-gray-500"> / </span>
                            <span className="text-blue-400">{vol.certificates_count}</span>
                          </td>
                          <td className="py-3 px-4 text-gray-400">{formatDate(vol.created_at)}</td>
                          <td className="py-3 px-4 text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                              onClick={() => handleDeleteAccount(vol.id, vol.name, 'volunteer')}
                              disabled={actionLoading === vol.id}
                            >
                              {actionLoading === vol.id ? '...' : <Trash2 size={18} />}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {volunteers.length === 0 && (
                    <p className="text-gray-400 text-center py-8">Aucun bénévole inscrit</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Certificates Tab */}
          <TabsContent value="certificates">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Toutes les attestations ({certificates.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Bénévole</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Association</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Mission</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Heures</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Statut</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Création</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {certificates.map((cert, idx) => (
                        <tr key={idx} className="border-b border-gray-700 hover:bg-gray-700/50">
                          <td className="py-3 px-4">
                            <div className="text-white font-medium">{cert.volunteer_name}</div>
                            <div className="text-gray-400 text-sm">{cert.volunteer_email}</div>
                          </td>
                          <td className="py-3 px-4 text-gray-300">{cert.association_name}</td>
                          <td className="py-3 px-4 text-gray-300 max-w-xs truncate">{cert.mission_description}</td>
                          <td className="py-3 px-4 text-center text-blue-400 font-medium">{cert.hours_volume}h</td>
                          <td className="py-3 px-4 text-center">
                            <Badge className={cert.status === 'validated' ? 'bg-green-600' : 'bg-yellow-600'}>
                              {cert.status === 'validated' ? 'Validée' : 'En attente'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-gray-400">{formatDate(cert.created_at)}</td>
                          <td className="py-3 px-4 text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                              onClick={() => handleDeleteCertificate(cert.id, cert.volunteer_name)}
                              disabled={actionLoading === cert.id}
                            >
                              {actionLoading === cert.id ? '...' : <Trash2 size={18} />}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {certificates.length === 0 && (
                    <p className="text-gray-400 text-center py-8">Aucune attestation</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default SuperAdminDashboard;
